(ns hai.home.mobile.drawer
  "Top-curtain menu drawer for the mobile dashboard. Renders the flat
   [[hai.web-app.layout/main-nav-items]] so mobile and desktop nav stay
   in sync."
  (:require
   [clojure.string :as str]
   [hai.home.mobile.state :as state]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]))

(def ^:private close-icon (icons/mui-icon :outlined :close))
(def ^:private chevron-right-icon (icons/mui-icon :outlined :chevron_right))
(def ^:private chevron-down-icon (icons/mui-icon :outlined :expand_more))

;; ---------------------------------------------------------------------------
;; Styled pieces
;; ---------------------------------------------------------------------------

(o/defstyled scrim :div
  {:position "fixed" :inset "0"
   :background "rgba(31,45,32,0.4)"
   :z-index "90"
   :opacity "0"
   :pointer-events "none"
   :transition "opacity 240ms ease"}
  [:&.open
   {:opacity "1" :pointer-events "auto"}])

(o/defstyled drawer-shell :div
  {:position "fixed"
   :top "0" :left "0" :right "0"
   :max-height "88vh"
   :background "#F7F6EF"
   :border-radius "0 0 24px 24px"
   :box-shadow "0 14px 36px rgba(31,45,32,0.20)"
   :z-index "100"
   :transform "translateY(-100%)"
   :transition "transform 320ms cubic-bezier(.2,.7,.3,1)"
   :display "flex" :flex-direction "column"
   :overflow "hidden"}
  [:&.open {:transform "translateY(0)"}])

(o/defstyled drawer-header :header
  {:display "flex" :align-items "center" :justify-content "space-between"
   :padding "8px 14px"
   :flex "0 0 auto"}
  [:.brand
   {:display "inline-flex" :align-items "center" :gap "8px"
    :padding "6px 10px"
    :border "none" :background "transparent"
    :border-radius "10px"
    :cursor "pointer"
    :font-family "inherit"
    :color t/--humble-primary-dark
    :transition "background 0.15s ease"}
   [:&:hover {:background "rgba(45,62,42,0.06)"}]]
  [:.brand-mark [:svg {:width "28px" :height "28px" :border-radius "7px"}]]
  [:.brand-wordmark
   {:font-weight "700" :font-size "17px" :letter-spacing "-0.01em"}]
  [:.brand-chevron
   {:display "inline-flex"
    :transform "rotate(180deg)"
    :transition "transform 0.2s ease"}
   [:svg {:width "18px" :height "18px" :fill "currentColor"}]]
  [:.close
   {:width "32px" :height "32px"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "1px solid #D9E0CE"
    :background "transparent" :border-radius "10px"
    :cursor "pointer" :color "#2D3E2A"
    :transition "background 0.15s ease"}
   [:&:hover {:background "rgba(184,196,166,0.10)"}]
   [:svg {:width "18px" :height "18px" :fill "currentColor"}]])

(o/defstyled drawer-body :div
  {:flex "1 1 auto" :overflow-y "auto"
   :padding "4px 0 8px"})

(o/defstyled nav-row :button
  {:display "flex" :align-items "center" :justify-content "space-between"
   :gap "12px"
   :width "100%"
   :padding "12px 24px"
   :border "none" :background "transparent"
   :font-family "inherit"
   :font-size "15px" :font-weight "500"
   :color "#2D3E2A"
   :cursor "pointer"
   :text-align "left"
   :border-left-width "3px"
   :border-left-style "solid"
   :border-left-color "transparent"
   :transition "background 0.15s ease, color 0.15s ease"}
  [:&:hover {:background "rgba(184,196,166,0.10)"}]
  [:&.active
   {:background t/--hai-white-2
    :border-left-color t/--humble-primary-dark
    :color t/--humble-primary-dark
    :font-weight "600"}]
  [:.row-icon
   {:display "inline-flex" :flex "0 0 auto"
    :width "20px" :height "20px"
    :align-items "center" :justify-content "center"
    :color t/--humble-primary-dark}
   [:svg {:width "20px" :height "20px" :fill "currentColor"}]
   [layout/initials-avatar {:width "20px" :height "20px" :font-size "9px"}]]
  [:.row-label {:flex "1" :min-width "0"
                :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}]
  [:.row-chevron
   {:display "inline-flex" :color "#6B7560"}
   [:svg {:width "18px" :height "18px" :fill "currentColor"}]])

(o/defstyled drawer-footer :div
  {:flex "0 0 auto"
   :background "#F7F6EF"
   :border-top "1px solid #D9E0CE"
   :padding "6px 0 4px"
   :display "flex" :flex-direction "column" :gap "4px"})

(o/defstyled grab-handle :div
  {:height "4px" :width "44px"
   :background "#D9E0CE"
   :border-radius "999px"
   :margin "6px auto 4px"
   :flex "0 0 auto"})

;; ---------------------------------------------------------------------------
;; Logic
;; ---------------------------------------------------------------------------

(defn- close! [] (>e [::state/set-menu-open? false]))

(defn- menu-row [{:keys [label route matches? icon active-route beta? developer-features?]}]
  (let [active?       (and matches? (matches? active-route))
        route-data    @(<s [:route/data route])
        resolved-icon (or icon (:icon route-data))]
    [nav-row
     {:type "button"
      :class (when active? "active")
      :on-click (fn [_]
                  (close!)
                  (>e [:routing/navigate [route]]))}
     (when resolved-icon
       [:span.row-icon [resolved-icon]])
     [:span.row-label label]
     (cond
       beta? [layout/nav-tag {:class "beta"} "Beta"]
       developer-features? [layout/nav-tag {:class "dev"} "Dev"])
     [:span.row-chevron [chevron-right-icon]]]))

(defn drawer []
  (let [open?        @(<s [::state/menu-open?])
        user         @(<s [:auth/identity])
        active-route @(<s [:routing/current-route-name])]
    [:<>
     [scrim {:class       (when open? "open")
             :on-click    close!
             :aria-hidden "true"}]
     [drawer-shell
      {:class       (when open? "open")
       :role        "dialog"
       :aria-modal  "true"
       :aria-hidden (if open? "false" "true")}
      [drawer-header
       [:button.brand
        {:type       "button"
         :aria-label "Close menu"
         :on-click   close!}
        [:span.brand-mark
         [icons/humble-logo-squidge-dark {:width 28 :height 28}]]
        [:span.brand-wordmark "Humble"]
        [:span.brand-chevron [chevron-down-icon]]]
       [:button.close
        {:type       "button"
         :aria-label "Close menu"
         :on-click   close!}
        [close-icon]]]
      [drawer-body
       (doall
        (for [{:keys [route label matches? icon beta? developer-features?] :as item} layout/main-nav-items
              :when (and route (layout/show-menu-item? item))]
          ^{:key (str route)}
          [menu-row {:label               label
                     :route               route
                     :matches?            (or matches? #{route})
                     :icon                icon
                     :active-route        active-route
                     :beta?               beta?
                     :developer-features? developer-features?}]))]
      [drawer-footer
       (when (:logged-in? user)
         [:div
          [menu-row (assoc layout/profile-nav-item :active-route active-route)]
          [:div {:style {:margin "-4px 4px 2px 56px" :font-size "15px"}
                 :on-click #(>e [:routing/navigate [:admin-ui/profile]])}
           (layout/pretty-email user)]])
       [grab-handle]]]]))
